import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import AppContext from '../../contexts/AppContext';
import { hasRole, ROLES, ROLE_HIERARCHY } from '../../utils/roleUtils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Users, UserPlus, Settings, Edit, Trash2, Filter, FilterX, Upload, RotateCcw, UserX, UserCheck, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { UserCreateDialog } from './components/UserCreateDialog';
import { UserEditDialog } from './components/UserEditDialog';
import { UserDeleteDialog } from './components/UserDeleteDialog';
import { UserImportDialog } from './components/UserImportDialog';
import { DataTable } from '../../components/DataTable';
import { AdminDashboardSkeleton } from './components/AdminDashboardSkeleton';
import TechnicianStatCard from './components/TechnicianStatCard';
import { hasUserPermission, PERMISSIONS } from '../../utils/permissionUtils';

// Technicians have their own screen (`AdminTechnicians`), so this one lists everyone else.
const LISTED_ROLES = ROLE_HIERARCHY.filter((role) => role !== ROLES.TECHNICIAN);

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AppContext);
  const isSuperAdmin = hasRole(currentUser?.role, 'superadmin');
  // Gate on the card, not inside it: an admin without the permission must not even fire the
  // directory call, which would answer 403.
  const canViewTechnicians = hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_TECHNICIANS_VIEW);

  // The API decides who may edit whom; the UI only reads the verdict
  const canEditTarget = (targetUser) => targetUser?.abilities?.canEdit === true;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [rowSelection, setRowSelection] = useState({});
  const [dialogState, setDialogState] = useState({
    create: false,
    edit: false,
    delete: false,
    import: false,
    bulkDeactivate: false,
    bulkReactivate: false,
  });

  const loadUsers = async () => {
    try {
      setLoading(true);
      const minDelay = new Promise(resolve => setTimeout(resolve, 600));
      const roleParam = LISTED_ROLES.join(',');
      const [response] = await Promise.all([fetchWithAuth(`/users?role=${roleParam}`), minDelay]);

      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const json = await response.json();

      if (json.success) {
        setUsers(json.data);
      } else {
        throw new Error(json.error || 'Failed to fetch users');
      }
    } catch (err) {
      console.error('Failed to load users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateUser = () => {
    setDialogState({ create: true, edit: false, delete: false, import: false });
  };

  const handleImportUsers = () => {
    setDialogState({ create: false, edit: false, delete: false, import: true });
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setDialogState({ create: false, edit: true, delete: false, import: false });
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setDialogState({ create: false, edit: false, delete: true, import: false });
  };

  const handleCloseDialogs = () => {
    setDialogState({ create: false, edit: false, delete: false, import: false, bulkDeactivate: false, bulkReactivate: false });
    setSelectedUser(null);
  };

  const handleUserCreated = () => {
    loadUsers();
    handleCloseDialogs();
  };

  const handleUserUpdated = () => {
    loadUsers();
    handleCloseDialogs();
  };

  const handleUserDeleted = () => {
    loadUsers();
    handleCloseDialogs();
  };

  const handleReactivateUser = async (user) => {
    try {
      const response = await fetchWithAuth(`/users/${user.id}/reactivate`, { method: 'PATCH' });
      const json = await response.json();
      if (json.success) {
        loadUsers();
      }
    } catch (err) {
      console.error('Error reactivating user:', err);
    }
  };

  const handleBulkDeactivate = () => {
    setDialogState(prev => ({ ...prev, bulkDeactivate: true }));
  };

  const handleBulkReactivate = () => {
    setDialogState(prev => ({ ...prev, bulkReactivate: true }));
  };

  const handleBulkDeactivateConfirmed = async () => {
    const selectedUserIds = getSelectedUsers().filter(u => u.isActive !== false).map(u => u.id);

    try {
      const response = await fetchWithAuth('/users/bulk-deactivate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedUserIds }),
      });

      const json = await response.json();
      if (json.success) {
        setRowSelection({});
        loadUsers();
      }
    } catch (err) {
      console.error('Error bulk deactivating users:', err);
    } finally {
      setDialogState(prev => ({ ...prev, bulkDeactivate: false }));
    }
  };

  const handleBulkReactivateConfirmed = async () => {
    const selectedUserIds = getSelectedUsers().filter(u => u.isActive === false).map(u => u.id);

    try {
      const response = await fetchWithAuth('/users/bulk-reactivate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedUserIds }),
      });

      const json = await response.json();
      if (json.success) {
        setRowSelection({});
        loadUsers();
      }
    } catch (err) {
      console.error('Error bulk reactivating users:', err);
    } finally {
      setDialogState(prev => ({ ...prev, bulkReactivate: false }));
    }
  };

  // Get selected users info for the dialogs
  const getSelectedUsers = () => {
    return Object.keys(rowSelection).map(idx => users[parseInt(idx)]).filter(Boolean);
  };

  // Format role for display and search
  const formatRole = (role) => {
    const roleConfig = {
      superadmin: t('Super Admin'),
      admin: t('Admin'),
      manager: t('Manager'),
      technician: t('Technician'),
      user: t('User')
    };
    return roleConfig[role] || roleConfig.user;
  };

  // Get unique service desks
  const uniqueServiceDesks = useMemo(() => {
    const desksMap = new Map();
    users.forEach(user => {
      const id = Array.isArray(user.serviceDeskId)
        ? user.serviceDeskId.join(',')
        : String(user.serviceDeskId);
      if (!desksMap.has(id)) {
        desksMap.set(id, {
          id,
          name: user.serviceDeskName
        });
      }
    });
    return Array.from(desksMap.values());
  }, [users]);

  // Custom filters render function
  const renderCustomFilters = ({ table, columnFilters }) => {
    const roleFilter = columnFilters.find(f => f.id === 'role')?.value || 'all';
    const serviceDeskFilter = columnFilters.find(f => f.id === 'serviceDeskName')?.value || 'all';
    const statusFilter = columnFilters.find(f => f.id === 'status')?.value || 'all';
    const hasActiveFilters = roleFilter !== 'all' || (isSuperAdmin && serviceDeskFilter !== 'all') || statusFilter !== 'all';

    return (
      <>
        <Select
          value={roleFilter}
          onValueChange={(value) => {
            table.getColumn('role')?.setFilterValue(value === 'all' ? undefined : value);
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('All roles')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('All roles')}</SelectItem>
            <SelectItem value={t('Super Admin')}>{t('Super Admin')}</SelectItem>
            <SelectItem value={t('Admin')}>{t('Admin')}</SelectItem>
            <SelectItem value={t('Manager')}>{t('Manager')}</SelectItem>
            <SelectItem value={t('User')}>{t('User')}</SelectItem>
          </SelectContent>
        </Select>

        {isSuperAdmin && (
          <Select
            value={serviceDeskFilter}
            onValueChange={(value) => {
              table.getColumn('serviceDeskName')?.setFilterValue(value === 'all' ? undefined : value);
            }}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder={t('All service desks')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('All service desks')}</SelectItem>
              {uniqueServiceDesks.map((desk) => (
                <SelectItem key={desk.id} value={desk.name}>
                  {desk.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select
          value={statusFilter}
          onValueChange={(value) => {
            table.getColumn('status')?.setFilterValue(value === 'all' ? undefined : value);
          }}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder={t('All statuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('All statuses')}</SelectItem>
            <SelectItem value={t('Active')}>{t('Active')}</SelectItem>
            <SelectItem value={t('Inactive')}>{t('Inactive')}</SelectItem>
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.resetColumnFilters()}
            className="gap-2"
          >
            <FilterX className="h-4 w-4" />
            {t('Clear filters')}
          </Button>
        )}
      </>
    );
  };

  // Table columns definition for TanStack Table
  const columns = useMemo(
    () => [
      {
        accessorKey: 'username',
        header: t('Username'),
        enableSorting: true,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className={row.original.isActive === false ? 'text-gray-400 line-through' : ''}>
              {row.original.username}
            </span>
            {row.original.isActive === false && (
              <span className="px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-500">{t('Inactive')}</span>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'email',
        header: t('Email'),
        enableSorting: true,
      },
      {
        accessorFn: (row) => formatRole(row.role),
        id: 'role',
        header: t('Role'),
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'includesString',
        cell: ({ row }) => {
          const roleConfig = {
            superadmin: { label: t('Super Admin'), className: 'bg-red-100 text-red-800' },
            admin: { label: t('Admin'), className: 'bg-red-100 text-red-800' },
            manager: { label: t('Manager'), className: 'bg-red-100 text-red-800' },
            technician: { label: t('Technician'), className: 'bg-gray-100 text-gray-800' },
            user: { label: t('User'), className: 'bg-blue-100 text-blue-800' }
          };
          const config = roleConfig[row.original.role] || roleConfig.user;
          return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
              {config.label}
            </span>
          );
        },
      },
      {
        accessorFn: (row) => row.serviceDeskName,
        id: 'serviceDeskName',
        header: t('Service Desk'),
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'equalsString',
        cell: ({ row }) => {
          const ids = Array.isArray(row.original.serviceDeskId)
            ? row.original.serviceDeskId.join(', ')
            : row.original.serviceDeskId;
          return (
            <div className="flex flex-col max-w-[220px]">
              <div className="font-medium truncate" title={row.original.serviceDeskName}>
                {row.original.serviceDeskName}
              </div>
              <div className="text-xs text-gray-500 truncate" title={`ID: ${ids}`}>
                ID: {ids}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'mode',
        header: t('Mode'),
        enableSorting: true,
        cell: ({ row }) => (
          <span className={`px-2 py-1 rounded-full text-xs ${
            row.original.mode === 'rw' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
            {row.original.mode === 'rw' ? t('Read / Write') : t('Read only')}
          </span>
        ),
      },
      {
        accessorFn: (row) => {
          if (row.sectors && row.sectors.length > 0) {
            return row.sectors.join(', ');
          }
          return '-';
        },
        id: 'sectors',
        header: t('Sectors'),
        enableSorting: true,
        cell: ({ row }) => (
          <div className="max-w-xs truncate" title={row.original.sectors?.join(', ')}>
            {row.original.sectors && row.original.sectors.length > 0 ? row.original.sectors.join(', ') : '-'}
          </div>
        ),
      },
      {
        accessorFn: (row) => row.isActive === false ? t('Inactive') : t('Active'),
        id: 'status',
        header: t('Status'),
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'equalsString',
        meta: { hidden: true },
      },
      {
        id: 'actions',
        header: t('Action'),
        enableSorting: false,
        cell: ({ row }) => {
          if (!canEditTarget(row.original)) return null;
          return (
          <div className="flex gap-2">
            <button
              onClick={() => handleEditUser(row.original)}
              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
              title={t('Edit')}
            >
              <Edit className="w-4 h-4" />
            </button>
            {row.original.isActive === false ? (
              <button
                onClick={() => handleReactivateUser(row.original)}
                className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                title={t('Reactivate')}
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={() => handleDeleteUser(row.original)}
                className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition-colors"
                title={t('Deactivate')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        );
        },
      },
    ],
    [handleEditUser, handleDeleteUser, handleReactivateUser, canEditTarget, t]
  );

  // Calculate statistics
  const activeUsers = users.filter(u => u.isActive !== false);
  const stats = {
    total: users.length,
    active: activeUsers.length,
    inactive: users.filter(u => u.isActive === false).length,
    admins: activeUsers.filter(u => u.role === 'superadmin' || u.role === 'admin').length,
    managers: activeUsers.filter(u => u.role === 'manager').length,
    users: activeUsers.filter(u => u.role === 'user').length
  };

  if (loading && users.length === 0) {
    return <AdminDashboardSkeleton />;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
        <h3 className="font-bold">{t('An error occurred')}</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="w-8 h-8" />
            {t('Users')}
          </h1>
          <p className="text-gray-500 mt-1">
            {t('Add or remove users')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleImportUsers} size="lg" variant="outline">
            <Upload className="w-4 h-4 mr-2" />
            {t('Import users')}
          </Button>
          <Button onClick={handleCreateUser} size="lg" className="bg-black hover:bg-gray-800">
            <UserPlus className="w-4 h-4 mr-2" />
            {t('Create a user')}
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className={`grid gap-4 md:grid-cols-2 ${canViewTechnicians ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`}>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('Total Users')}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('{{active}} active, {{inactive}} inactive', { active: stats.active, inactive: stats.inactive })}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('Administrators')}</CardTitle>
            <Badge variant="destructive" className="h-4 px-2">Admin</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.admins}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('Accounts with admin privileges')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('Managers')}</CardTitle>
            <Badge variant="secondary" className="h-4 px-2">Manager</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.managers}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('Accounts with manager privileges')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('Users')}</CardTitle>
            <Badge variant="outline" className="h-4 px-2">User</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.users}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('Standard users')}
            </p>
          </CardContent>
        </Card>

        {canViewTechnicians && <TechnicianStatCard />}
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('Manage users')}</CardTitle>
          <CardDescription>
            {t('View, create, edit or deactivate user accounts')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            data={users}
            columns={columns}
            emptyMessage={t('No user found')}
            showGlobalSearch={true}
            showPagination={true}
            pageSize={50}
            customFilters={renderCustomFilters}
            enableRowSelection={true}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            canSelectRow={(user) => canEditTarget(user)}
            bulkActionsBar={() => {
              const selected = getSelectedUsers();
              const activeCount = selected.filter(u => u.isActive !== false).length;
              const inactiveCount = selected.filter(u => u.isActive === false).length;
              return (
                <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                  <span className="text-sm font-medium text-blue-800">
                    {t('{{count}} user selected', { count: selected.length })}
                    {activeCount > 0 && inactiveCount > 0 && (
                      <span className="text-blue-600 ml-1">
                        {t('({{active}} active, {{inactive}} inactive)', { active: activeCount, inactive: inactiveCount })}
                      </span>
                    )}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRowSelection({})}
                      className="gap-1"
                    >
                      <X className="h-3 w-3" />
                      {t('Deselect')}
                    </Button>
                    {activeCount > 0 && (
                      <Button
                        size="sm"
                        onClick={handleBulkDeactivate}
                        className="gap-1 bg-amber-600 hover:bg-amber-700"
                      >
                        <UserX className="h-3 w-3" />
                        {t('Deactivate ({{count}})', { count: activeCount })}
                      </Button>
                    )}
                    {inactiveCount > 0 && (
                      <Button
                        size="sm"
                        onClick={handleBulkReactivate}
                        className="gap-1 bg-green-600 hover:bg-green-700"
                      >
                        <UserCheck className="h-3 w-3" />
                        {t('Reactivate ({{count}})', { count: inactiveCount })}
                      </Button>
                    )}
                  </div>
                </div>
              );
            }}
          />
        </CardContent>
      </Card>

      {/* Dialogs */}
      <UserCreateDialog
        open={dialogState.create}
        onClose={handleCloseDialogs}
        onUserCreated={handleUserCreated}
        defaultRole="user"
      />

      <UserEditDialog
        open={dialogState.edit}
        user={selectedUser}
        onClose={handleCloseDialogs}
        onUserUpdated={handleUserUpdated}
      />

      <UserDeleteDialog
        open={dialogState.delete}
        user={selectedUser}
        onClose={handleCloseDialogs}
        onUserDeleted={handleUserDeleted}
      />

      <UserImportDialog
        open={dialogState.import}
        onClose={handleCloseDialogs}
        onUsersImported={loadUsers}
        allowedRoles={null}
      />

      {/* Bulk Deactivate Dialog */}
      <AlertDialog open={dialogState.bulkDeactivate} onOpenChange={() => setDialogState(prev => ({ ...prev, bulkDeactivate: false }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <UserX className="h-5 w-5 text-amber-600" />
              <AlertDialogTitle>{t('Bulk deactivation')}</AlertDialogTitle>
            </div>
            <AlertDialogDescription asChild>
              <div>
                <p>
                  {t('Are you sure you want to deactivate {{count}} user?', { count: getSelectedUsers().length })}
                </p>
                <ul className="mt-3 max-h-40 overflow-y-auto space-y-1 text-sm">
                  {getSelectedUsers().map(user => (
                    <li key={user.id} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                      {user.username} ({user.email})
                    </li>
                  ))}
                </ul>
                <p className="mt-3">
                  {t('These users can no longer log in. You can reactivate them later.')}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDeactivateConfirmed}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {t('Deactivate ({{count}})', { count: getSelectedUsers().length })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Bulk Reactivate Dialog */}
      <AlertDialog open={dialogState.bulkReactivate} onOpenChange={() => setDialogState(prev => ({ ...prev, bulkReactivate: false }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-green-600" />
              <AlertDialogTitle>{t('Bulk reactivation')}</AlertDialogTitle>
            </div>
            <AlertDialogDescription asChild>
              <div>
                <p>
                  {t('Are you sure you want to reactivate {{count}} user?', { count: getSelectedUsers().filter(u => u.isActive === false).length })}
                </p>
                <ul className="mt-3 max-h-40 overflow-y-auto space-y-1 text-sm">
                  {getSelectedUsers().filter(u => u.isActive === false).map(user => (
                    <li key={user.id} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      {user.username} ({user.email})
                    </li>
                  ))}
                </ul>
                <p className="mt-3">
                  {t('These users will be able to log in again.')}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkReactivateConfirmed}
              className="bg-green-600 hover:bg-green-700"
            >
              {t('Reactivate ({{count}})', { count: getSelectedUsers().filter(u => u.isActive === false).length })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
